/* istanbul ignore file */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import useFilteredCatalogLatestVersion from '../../../helpers/utilityCustomHooks/useFilteredCatalogLatestVersion';
import searchIcon from '@svb/svbbranding/dist/assets/icons/Search_grey.svg';
const normalizePath = (value = '') => {
  const path = String(value).split('?')[0].split('#')[0].replace(/\/+$/, '');
  return path || '/';
};

const RebrandedNavHeader = ({
  menus = [],
  logo = '',
  logoAlt = 'First Citizens Bank logo',
  defaultApiCategoryId = null,
  showSearch = false,
  onSearchClick,
}) => {
  const navbarRef = useRef(null);
  const { pathname } = useLocation();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const apiDocsMenu = useMemo(() => {
    return (
      menus.find(
        (menu) => menu.parentRouteName === 'apis' || menu.name?.trim().toLowerCase() === 'api docs',
      ) || null
    );
  }, [menus]);

  const apiCategories = useMemo(() => {
    return Array.isArray(apiDocsMenu?.subMenu) ? apiDocsMenu.subMenu : [];
  }, [apiDocsMenu]);

  const activeCategory = useMemo(() => {
    return (
      apiCategories.find((category) => String(category.id) === String(activeCategoryId)) || null
    );
  }, [apiCategories, activeCategoryId]);

  // const apiItems = useMemo(() => {
  //   return useFilteredCatalogLatestVersion(activeCategory?.subMenu || [])
  // }, [activeCategory]);
  const apiItems = useFilteredCatalogLatestVersion(activeCategory?.subMenu || []);

  const hasSubMenu = (item) => {
    return Array.isArray(item?.subMenu) && item.subMenu.length > 0;
  };

  const getLink = (item) => {
    if(item?.name === 'API Docs') {
      return '/api-docs';
    }

    if (item?.path) {
      return item.path;
    }

    if (item?.link) {
      return item.link;
    }

    if (item?.parentRouteName) {
      return `/${item.parentRouteName}`;
    }

    return '/';
  };

  const getApiLink = (api) => {
    const children = Array.isArray(api?.subMenu) ? api.subMenu : [];
    const documentationChild = children.find(
      (child) => child.type === 'docs' && (child.path || child.link),
    );

    const firstRoutableChild = children.find((child) => child.path || child.link);
    return (
      documentationChild?.path ||
      documentationChild?.link ||
      firstRoutableChild?.path ||
      firstRoutableChild?.link ||
      api?.path ||
      api?.link ||
      '/apis'
    );
  };

  const isPathActive = (value) => {
    const targetPath = normalizePath(value);
    const currentPath = normalizePath(pathname);
    if (targetPath === '/') {
      return currentPath === '/';
    }

    return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
  };

  const isTreeActive = (item) => {
    if (!item) {
      return false;
    }

    const itemPath = getLink(item);

    if (itemPath !== '/' && isPathActive(itemPath)) {
      return true;
    }

    return item.subMenu?.some((child) => isTreeActive(child)) || false;
  };

  const isApiDocsRouteActive = () => {
    const currentPath = normalizePath(pathname);
    if (currentPath === '/apis' || currentPath === '/apis/docs-home') {
      return false;
    }
    return currentPath.startsWith('/apis/');
  };

  const getDefaultCategory = () => {
    const configuredCategory =
      defaultApiCategoryId !== null
        ? apiCategories.find((category) => String(category.id) === String(defaultApiCategoryId))
        : null;
    const routeCategory = apiCategories.find((category) => isTreeActive(category));
    return configuredCategory || routeCategory || apiCategories[0] || null;
  };

  useEffect(() => {
    const routeCategory = apiCategories.find((category) => isTreeActive(category));
    if (routeCategory) {
      setActiveCategoryId(routeCategory.id);
    }
  }, [pathname, apiCategories]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('pointerdown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const toggleMenu = (menu) => {
    const menuIsOpen = String(openMenuId) === String(menu.id);
    if (menuIsOpen) {
      setOpenMenuId(null);
      return;
    }
    setOpenMenuId(menu.id);
    if (String(menu.id) === String(apiDocsMenu?.id)) {
      const defaultCategory = getDefaultCategory();
      setActiveCategoryId(defaultCategory?.id ?? null);
    }
  };

  const closeMenu = () => {
    setOpenMenuId(null);
  };

  // console.info(activeCategoryId, 'active catid');
  // console.info(activeCategory, "active cat");
  // console.info(apiItems, "apiItems");
  // console.info(apiItems.length, "api item Length");
  return (
    <nav
      ref={navbarRef}
      className='main-navbar d-flex align-itmes-center justify-content-start px-4 gap-4'
      aria-label='Primary navigation'
    >
      <Link
        className='main-navbar-brand d-flex align-items-center'
        to='/apis/docs-home'
        onClick={closeMenu}
      >
        {logo && (
          <img
            src={logo}
            className='navbar-brand firstcitizens-logo'
            alt={logoAlt}
            width='191'
            height='50'
          />
        )}
      </Link>

      <ul className='main-navbar-menu d-flex flex-row justify-content-center align-items-stretch m-0 p-0 list-unstyled'>
        {menus.map((menu) => {
          const menuHasSubMenu = hasSubMenu(menu);
          const menuIsApiDocs = String(menu.id) === String(apiDocsMenu?.id);
          const menuIsOpen = String(openMenuId) === String(menu.id);
          const menuIsActive = menuIsApiDocs
            ? menuIsOpen || isApiDocsRouteActive()
            : menuIsOpen || isTreeActive(menu);

          return (
            <li
              className='main-navbar-item position-relative d-flex align-items-stretch'
              key={menu.id}
            >
              {menuHasSubMenu ? (
                <button
                  type='button'
                  className={`main-navbar-link main-navbar-button position-relative d-flex align-items-center justify-content-center ${
                    menuIsActive ? 'main-navbar-link-active' : ''
                  }`}
                  aria-haspopup='true'
                  aria-expanded={menuIsOpen}
                  onClick={() => toggleMenu(menu)}
                >
                  <span>{menu.name}</span>

                  <i
                    className={`fa fa-caret-down nav-chevron ${
                      menuIsOpen ? 'nav-chevron-open' : ''
                    }`}
                    aria-hidden='true'
                  />
                </button>
              ) : (
                <NavLink
                  end={getLink(menu) === '/'}
                  to={getLink(menu)}
                  onClick={closeMenu}
                  className={({ isActive }) =>
                    `main-navbar-link position-relative d-flex align-items-center justify-content-center ${isActive ? 'main-navbar-link-active' : ''}`
                  }
                >
                  {menu.name}
                </NavLink>
              )}

              {menuIsApiDocs && menuIsOpen && (
                <div
                  className='api-mega-menu position-absolute top-100 start-0 d-flex overflow-auto'
                  aria-label='API Docs menu'
                >
                  <div className='api-category-column'>
                    <ul className='dropdown-list list-unstyled w-100 m-0 p-0'>
                      {apiCategories.map((category) => {
                        const categoryIsSelected = String(activeCategoryId) === String(category.id);
                        const categoryRouteIsActive = isTreeActive(category);
                        const categoryIsActive = categoryIsSelected || categoryRouteIsActive;

                        return (
                          <li key={category.id} className='p-0 m-0'>
                            <button
                              type='button'
                              className={`dropdown-item-button ${
                                categoryIsActive ? 'active-dropdown-item' : ''
                              }`}
                              aria-pressed={categoryIsSelected}
                              onClick={() => setActiveCategoryId(category.id)}
                            >
                              <span>{category.name}</span>

                              {hasSubMenu(category) && (
                                <i
                                  className='fa fa-chevron-right category-arrow'
                                  aria-hidden='true'
                                />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className='api-items-column'>
                    {!activeCategory ? (
                      <p className='dropdown-message'>Select an API category</p>
                    ) : apiItems.length === 0 ? (
                      <p className='dropdown-message'>No APIs are available</p>
                    ) : (
                      <ul className='dropdown-list api-items-list w-100 m-0 p-0'>
                        {apiItems.map((api) => {
                          const apiPath = getApiLink(api);
                          const apiIsActive = isPathActive(apiPath) || isTreeActive(api);

                          return (
                            <li key={api.id} className='p-0 m-0'>
                              <NavLink
                                to={apiPath}
                                onClick={closeMenu}
                                className={`api-item-link ${apiIsActive ? 'active-api-item' : ''}`}
                              >
                                {api.name}
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {!menuIsApiDocs && menuHasSubMenu && menuIsOpen && (
                <div className='standard-dropdown' aria-label={`${menu.name} menu`}>
                  <ul className='dropdown-list list-unstyled w-100 m-0 p-0'>
                    {menu.subMenu.map((subMenuItem) => (
                      <li key={subMenuItem.id} className='p-0 m-0'>
                        <NavLink
                          end
                          to={getLink(subMenuItem)}
                          onClick={closeMenu}
                          className={({ isActive }) =>
                            `standard-dropdown-link ${isActive ? 'active-dropdown-link' : ''}`
                          }
                        >
                          {subMenuItem.name}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* <div className='main-navbar-search'> */}
      {showSearch && (
        <div className='d-flex flex-fill justify-content-end align-items-center'>
          <a href='#' onClick={onSearchClick}>
            <img src={searchIcon} alt='search icon' role='presentation' width='20' height='20' />
          </a>
        </div>
      )}
      {/* </div> */}
    </nav>
  );
};

export default RebrandedNavHeader;
